/**
 * Who may touch a ball in flight, and with what (owner: ball team). Shared by the catch/swat detection and the
 * controller's glove tracking so the glove only opens toward balls it is actually allowed to catch.
 */
import { CATCH, DT, ONSIDE_MIN_YARDS, SPECIALS_TUNING } from '@hf/shared';
import { BallState, GlovePose, Phase, PlayCall, PlayerState } from '../enums';
import { PF } from '../player/fsm';
import { offenseCatchIsLegal } from '../rules/helpers';
import type { PlayerSim, SimState } from '../state';

/** The ball is a live pass / toss / tip that can still be caught. */
export const ballInPlayAir = (s: SimState): boolean =>
  s.ball.state === BallState.AIR && (s.rules.phase === Phase.LIVE || s.rules.phase === Phase.RUSH_COUNT);

/** The thrower cannot touch his own release for CATCH.throwerIgnoreFrames. */
export const throwerIgnored = (s: SimState, p: PlayerSim): boolean =>
  s.ball.thrownBy === p.index && s.ball.airFrames <= CATCH.throwerIgnoreFrames;

/**
 * The player may touch the flight at all (glove or head). The OFFENSE may touch it only where a catch would be
 * legal (ball AND head beyond the LOS, not out of the back): a QB never brushes his own lob behind the line.
 */
export const mayTouchFlight = (s: SimState, p: PlayerSim): boolean => {
  if (throwerIgnored(s, p) || (p.flags & PF.SWATTED) !== 0) return false;
  if (p.state === PlayerState.CELEBRATE) return false;
  // A KICK in the air belongs to the receiver: the kicker may not touch it (kick-catch interference).
  if (s.ball.kick) return p.index !== s.rules.offense;
  return p.index !== s.rules.offense || offenseCatchIsLegal(s, p.index);
};

/** The glove is free to catch (not stunned, tackled, throwing or posing). */
export const gloveFree = (p: PlayerSim): boolean => {
  switch (p.state) {
    case PlayerState.TACKLED:
    case PlayerState.STUNNED:
    case PlayerState.CELEBRATE:
    case PlayerState.THROW_RELEASE:
    case PlayerState.HARD_COUNT:
    case PlayerState.SPECIAL_POSE:
    case PlayerState.STIFF_ARM:
    case PlayerState.FAKE_SLAM:
      return false;
    default:
      return true;
  }
};

/** Catch radius for this player on the current flight (Hail Mary: the thrower's own radius grows). */
export const catchRadiusFor = (s: SimState, p: PlayerSim): number =>
  CATCH.gloveCatchRadius *
  (s.ball.hailMary && s.ball.thrownBy === p.index ? SPECIALS_TUNING.hailMary.catchRadiusMul : 1);

/**
 * The glove is COMMITTED to the ball: a lunge reaching for it (REACH pose: dive / leap) or an airborne player
 * (he jumped for it — a commitment a pump fake can bait).
 */
export const gloveCommitted = (p: PlayerSim): boolean => p.glovePose === GlovePose.REACH || !p.grounded;

/**
 * May `p`'s glove CATCH this flight (not just swat it)? The offense's receiver: any open glove (where the catch is
 * legal, see `mayTouchFlight`). The DEFENSE intercepts only with a committed glove (`gloveCommitted`) while SET on
 * the ball (`setOnBall`): a passive, grounded defender's tracking glove, or a trailing one, can only break the pass
 * up (swat). Interceptions cost a read.
 */
export const mayCatchFlight = (s: SimState, p: PlayerSim): boolean =>
  p.index === s.rules.offense || s.ball.kick || (gloveCommitted(p) && setOnBall(s, p));

/**
 * The defender is SET on the flight (CATCH.interceptNeedsFacing): the ball is in front of him and coming at him
 * (or he is running it down). A trailing defender — the pass dropping over his shoulder — can only swat it.
 */
export const setOnBall = (s: SimState, p: PlayerSim): boolean => {
  if (!CATCH.interceptNeedsFacing) return true;
  const b = s.ball;
  const rvx = b.vx - p.vx;
  if (p.facing * rvx > 0) return false; // dropping over his shoulder (or he is turned away from it)
  // in front of him at the start or the end of this tick (a fast ball can cross the glove within one tick)
  const front = p.facing * (b.x - p.x);
  const frontBefore = front - p.facing * rvx * DT;
  return (front > frontBefore ? front : frontBefore) >= -CATCH.interceptBehindSlop;
};

/** A LOOSE kick in a live play (Classic). */
export const looseInPlay = (s: SimState): boolean =>
  s.ball.state === BallState.LOOSE &&
  s.ball.kick &&
  (s.rules.phase === Phase.LIVE || s.rules.phase === Phase.RUSH_COUNT);

/**
 * May `p` pick up the LOOSE kick (ARCHITECTURE §5 "Classic format")? The receiver always. The kicker: a free kick
 * (kickoff / onside) once it is ONSIDE_MIN_YARDS past the LOS or the receiver touched it first; a punt always — an
 * untouched punt is DOWNED by his touch (the receiver's ball there), a muffed one is his.
 */
export const mayRecoverLoose = (s: SimState, p: PlayerSim): boolean => {
  const r = s.rules;
  const b = s.ball;
  if (!b.kick || p.state === PlayerState.CELEBRATE) return false;
  if (p.index !== r.offense) return true;
  if (r.playCall === PlayCall.PUNT) return true;
  return (b.touchedMask & (1 << (1 - r.offense))) !== 0 || (b.x - r.los) * r.attackDir >= ONSIDE_MIN_YARDS;
};

/**
 * Release grace (CATCH.releaseGraceFrames): right after the release a NON-thrower's glove that is not lunging
 * (REACH) cannot touch the ball, so a defender parked at the line cannot glove a pass on its release tick. Head
 * contact still deflects it.
 */
export const inReleaseGrace = (s: SimState, p: PlayerSim): boolean =>
  s.ball.thrownBy >= 0 &&
  s.ball.thrownBy !== p.index &&
  s.ball.airFrames <= CATCH.releaseGraceFrames &&
  p.glovePose !== GlovePose.REACH;
